import type { CSSProperties, FC, ReactNode } from 'react'

import Image from 'next/image'
import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ButtonLink } from '@/components/generals/button'
import { Tilt } from '@/components/generals/tilt'
import { Scallop } from '@/components/generals/scallop'
import { SectionHeading } from '@/components/generals/section-heading'
import { PROJECTS } from '@/constants/projects'

export const Work: FC = (): ReactNode => {
    return (
        <section id='work' aria-labelledby='work-title' className='relative bg-cream py-24 sm:py-32'>
            <Scallop className='text-cream' />

            <div className='mx-auto max-w-6xl px-5 sm:px-8'>
                <SectionHeading
                    id='work-title'
                    lines={['Fresh out', 'of the studio']}
                    sticker='Our work'
                    stickerShape='flower'
                    stickerColor='text-lavender'
                />

                <ul className='mt-16 grid gap-x-8 gap-y-14 md:grid-cols-2'>
                    {PROJECTS.map((project, index) => (
                        <li
                            key={project.slug}
                            className={cn(index % 2 === 0 ? 'reveal-left' : 'reveal-right md:mt-20')}
                            style={{ '--reveal-delay': `${(index % 2) * 150}ms` } as CSSProperties}
                        >
                            <article className='group'>
                                <Tilt max={6}>
                                <Link
                                    href={`/work/${project.slug}`}
                                    tabIndex={-1}
                                    aria-hidden='true'
                                    className={cn(
                                        'relative block aspect-[4/3] overflow-hidden rounded-[2rem] border-2 border-plum shadow-[0_6px_0_0_var(--color-plum)] transition-[translate,box-shadow] duration-300 group-hover:-translate-y-2 group-hover:shadow-[0_12px_0_0_var(--color-plum)]',
                                        project.color
                                    )}
                                >
                                    <Image
                                        src={project.image}
                                        alt=''
                                        fill
                                        sizes='(min-width: 1152px) 540px, (min-width: 768px) 46vw, 92vw'
                                        className='object-cover transition-transform duration-700 ease-out group-hover:scale-110 group-hover:-rotate-2'
                                    />

                                    <span className='absolute top-4 left-4 rounded-full border-2 border-plum bg-cream px-3 py-1 font-heading text-base tracking-wide text-plum transition-all duration-300 group-hover:-rotate-6 group-hover:bg-butter'>
                                        {project.category}
                                    </span>

                                    <span className='absolute right-4 bottom-4 grid size-14 translate-y-4 scale-50 place-items-center rounded-full border-2 border-plum bg-butter text-plum opacity-0 transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:translate-y-0 group-hover:scale-100 group-hover:rotate-12 group-hover:opacity-100'>
                                        <ArrowUpRight className='size-6' />
                                    </span>
                                </Link>
                                </Tilt>

                                <div className='mt-6 flex flex-wrap items-end justify-between gap-4'>
                                    <div className='min-w-0'>
                                        <h3 className='font-heading text-[clamp(2rem,3.6vw,2.75rem)] leading-none'>
                                            {project.name}
                                        </h3>

                                        <p className='mt-2 max-w-sm text-plum/75'>
                                            {project.summary}
                                        </p>
                                    </div>

                                    <ButtonLink href={`/work/${project.slug}`} size='sm'>
                                        View case study
                                        <span className='sr-only'>
                                            {`: ${project.name}`}
                                        </span>
                                    </ButtonLink>
                                </div>
                            </article>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    )
}
